import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/dateFormat";
import { AlertTriangle, TrendingUp, Users, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, ReferenceArea, LabelList,
} from "recharts";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const TYPE_COLORS = {
  general: "#0EA5E9", sick: "#EF4444", probation: "#EAB308",
  short_break: "#22C55E", bereavement: "#8B5CF6",
};
const TYPE_LABELS = {
  general: "General", sick: "Sick", probation: "Probation",
  short_break: "Short break", bereavement: "Bereavement",
};
const LINE_COLORS = ["#0EA5E9", "#F97316", "#22C55E", "#8B5CF6", "#EC4899", "#14B8A6", "#EAB308", "#64748B"];
const LOW_BALANCE_THRESHOLD = 2;

function leaveBarTone(remaining) {
  if (remaining <= 1) return "text-red-600";
  if (remaining <= 3) return "text-orange-600";
  return "text-slate-700";
}

/** Segment label inside a stacked bar showing raw `taken/entitled` for that
 * category — skipped when the segment is too narrow to hold readable text. */
function makeSegmentLabel(type, rows) {
  return function SegmentLabel(props) {
    const { x, y, width, height, index } = props;
    const row = rows[index];
    const meta = row && row[`${type}_meta`];
    if (!meta || !meta.taken || width < 26) return null;
    return (
      <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={600} fill="#fff">
        {`${meta.taken}/${meta.entitled}`}
      </text>
    );
  };
}

/** Y-axis category tick — renders the person's name in red + bold when
 * they have any leave category at LOW_BALANCE_THRESHOLD days remaining or fewer,
 * plus a small amber dot when any category includes a manual leave_adjustments correction. */
function makeAtRiskTick(rows, atRiskIds, adjustedIds) {
  return function AtRiskTick(props) {
    const { x, y, index, payload } = props;
    const row = rows[index];
    const flagged = row && atRiskIds.has(row.user_id);
    const adjusted = row && adjustedIds.has(row.user_id);
    return (
      <g>
        <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fontWeight={flagged ? 700 : 400} fill={flagged ? "#dc2626" : "#334155"}>
          {payload.value}
        </text>
        {adjusted && <circle cx={x + 4} cy={y - 8} r={3} fill="#f59e0b" data-testid={`insights-adjusted-dot-${row.user_id}`} />}
      </g>
    );
  };
}

/** Custom tooltip for the Team leave distribution chart — same info as the
 * default recharts tooltip, but appends "(adjusted)" next to any category
 * that includes a manual leave_adjustments correction for that person. */
function TeamDistTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  const adjustedTypes = new Set(row?.adjusted_types || []);
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm px-2.5 py-2 text-xs space-y-1">
      <div className="font-semibold text-slate-800">{label}</div>
      {payload.filter((p) => p.value > 0).map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: p.color }} />
          <span className="text-slate-700">{TYPE_LABELS[p.dataKey] || p.dataKey}: {p.value}</span>
          {adjustedTypes.has(p.dataKey) && <span className="text-amber-600 font-medium">(adjusted)</span>}
        </div>
      ))}
    </div>
  );
}

/** Admin-only Insights suite for the Leaves page: team distribution, monthly
 * trend, leave balances, and rule-based high-risk / forecast panels.
 * `allLeaves` is passed down from Leaves.jsx so we don't double-fetch.
 * `isFounder` gates the manual balance-adjustment control (founder only —
 * admins see the same values read-only, per the founder-only requirement). */
export default function LeavesInsights({ allLeaves, isFounder }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState("all");
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustTakenDelta, setAdjustTakenDelta] = useState("");
  const [adjustEntitlementDelta, setAdjustEntitlementDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try {
      const [{ data: insights }, { data: bal }] = await Promise.all([
        api.get("/leaves/insights", { params: { year: y } }),
        api.get("/leaves/balance/all", { params: { year: y } }),
      ]);
      setData(insights);
      setBalances(bal);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); }, [year]);

  const balancesByUser = useMemo(() => {
    const map = {};
    (balances?.users || []).forEach((u) => { map[u.user_id] = u; });
    return map;
  }, [balances]);

  // Anyone with ANY of the 5 displayed categories at 2-or-fewer remaining —
  // flagged red everywhere below. The API also returns casual/earned (legacy
  // types, not shown in this UI) so we scope the check to TYPE_COLORS only.
  const atRiskUserIds = useMemo(() => {
    const set = new Set();
    (balances?.users || []).forEach((u) => {
      if (u.balances.some((b) => b.leave_type in TYPE_COLORS && b.remaining <= LOW_BALANCE_THRESHOLD)) set.add(u.user_id);
    });
    return set;
  }, [balances]);

  // Team Leave Distribution — sorted desc, capped to a readable height.
  // Each category also carries `${type}_meta` (taken/entitled) for the
  // in-bar segment labels, sourced from the same balances used in the table below.
  const teamChartData = useMemo(() => {
    if (!data?.team_distribution) return [];
    return data.team_distribution.map((r) => {
      const bu = balancesByUser[r.user_id];
      const row = { name: r.name, user_id: r.user_id, ...r.by_type, total: r.total, adjusted_types: r.adjusted_types || [] };
      Object.keys(TYPE_COLORS).forEach((t) => {
        const b = bu?.balances?.find((x) => x.leave_type === t);
        row[`${t}_meta`] = { taken: b ? b.taken : (r.by_type?.[t] || 0), entitled: b ? b.entitled : 0 };
      });
      return row;
    });
  }, [data, balancesByUser]);

  // People with a manual leave_adjustments correction on any category —
  // marked with a small amber dot on the Y-axis + noted in the tooltip.
  const adjustedUserIds = useMemo(() => {
    const set = new Set();
    teamChartData.forEach((r) => { if (r.adjusted_types && r.adjusted_types.length) set.add(r.user_id); });
    return set;
  }, [teamChartData]);


  // Monthly Distribution — either the selected individual's per-type stack,
  // or the top-8 people's totals-per-month as separate lines.
  const topPeople = useMemo(() => (data?.team_distribution || []).slice(0, 8).map((r) => r.user_id), [data]);
  const monthlyChartData = useMemo(() => {
    if (!data?.monthly_distribution) return [];
    if (selectedUser !== "all") {
      // Need per-type breakdown for the selected person, month by month.
      const byMonth = {};
      (allLeaves || []).filter((l) => l.user_id === selectedUser && l.status === "approved").forEach((l) => {
        const sd = new Date(l.start_date), ed = new Date(l.end_date);
        if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return;
        const isHalf = (l.half_day === "first_half" || l.half_day === "second_half") && l.start_date === l.end_date;
        const weight = isHalf ? 0.5 : 1;
        for (let d = new Date(sd); d <= ed; d.setDate(d.getDate() + 1)) {
          const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          if (!m.startsWith(String(year))) continue;
          byMonth[m] = byMonth[m] || { month: m };
          byMonth[m][l.leave_type] = (byMonth[m][l.leave_type] || 0) + weight;
        }
      });
      return Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
    }
    return data.monthly_distribution.map((row) => {
      const out = { month: row.month };
      topPeople.forEach((uid) => { out[data.user_names[uid]] = row.by_user[uid] || 0; });
      return out;
    });
  }, [data, selectedUser, allLeaves, year, topPeople]);

  const individualLeaves = useMemo(() => {
    if (selectedUser === "all") return [];
    return (allLeaves || []).filter((l) => l.user_id === selectedUser).sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
  }, [allLeaves, selectedUser]);

  const peopleForSelect = data?.team_distribution || [];

  const openAdjust = (u, leaveType, b) => {
    setAdjustTarget({ user_id: u.user_id, name: u.name, leave_type: leaveType, entitled: b.entitled, taken: b.taken, remaining: b.remaining });
    setAdjustTakenDelta("");
    setAdjustEntitlementDelta("");
    setAdjustReason("");
  };

  const takenDeltaNum = parseFloat(adjustTakenDelta) || 0;
  const entitlementDeltaNum = parseFloat(adjustEntitlementDelta) || 0;
  const previewTaken = adjustTarget ? Math.max(0, adjustTarget.taken + takenDeltaNum) : 0;
  const previewEntitled = adjustTarget ? adjustTarget.entitled + entitlementDeltaNum : 0;
  const previewRemaining = Math.max(0, previewEntitled - previewTaken);

  const submitAdjust = async () => {
    if (!takenDeltaNum && !entitlementDeltaNum) {
      toast.error("Enter a non-zero number of days in at least one field (e.g. 2, -1, or 0.5)");
      return;
    }
    setAdjusting(true);
    const reason = adjustReason.trim() || undefined;
    const applied = [];
    try {
      // Sequential (not Promise.all): if the 2nd call fails after the 1st
      // succeeds, we still know exactly which one landed and can say so.
      if (takenDeltaNum) {
        await api.post("/leaves/balance/adjust", {
          user_id: adjustTarget.user_id, leave_type: adjustTarget.leave_type,
          year, delta: takenDeltaNum, adjustment_type: "taken", reason,
        });
        applied.push(`taken ${takenDeltaNum > 0 ? "+" : ""}${takenDeltaNum}`);
      }
      if (entitlementDeltaNum) {
        await api.post("/leaves/balance/adjust", {
          user_id: adjustTarget.user_id, leave_type: adjustTarget.leave_type,
          year, delta: entitlementDeltaNum, adjustment_type: "entitlement", reason,
        });
        applied.push(`entitlement ${entitlementDeltaNum > 0 ? "+" : ""}${entitlementDeltaNum}`);
      }
      toast.success(`Adjusted ${adjustTarget.name}'s ${TYPE_LABELS[adjustTarget.leave_type]} balance (${applied.join(", ")})`);
      setAdjustTarget(null);
      load(year);
    } catch (e) {
      if (applied.length) {
        toast.error(`Applied ${applied.join(", ")}, but the next adjustment failed: ${formatApiError(e)}`);
        load(year);
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setAdjusting(false); }
  };

  return (
    <div className="space-y-6" data-testid="leaves-insights">
      <div className="flex items-center justify-between">
        <div className="label-overline">Insights · {loading ? "loading…" : year}</div>
        <div className="inline-flex items-center gap-1">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                y === year ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`insights-year-${y}`}
            >{y}</button>
          ))}
        </div>
      </div>

      {/* Team Leave Distribution */}
      <div className="card-flat p-4" data-testid="chart-team-distribution">
        <div className="text-sm font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5"><Users size={14} />Team leave distribution</div>
        <div className="text-xs text-slate-500 mb-3">
          Total leave days per person this year, stacked by category — highest first. Segment labels show taken/entitled; a name in red means at least one category has {LOW_BALANCE_THRESHOLD} or fewer days remaining.
          <span className="inline-flex items-center gap-1 ml-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> amber dot = includes a manual adjustment</span>
        </div>
        <ResponsiveContainer width="100%" height={Math.max(220, teamChartData.length * 34)}>
          <BarChart data={teamChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={true} fontSize={11} />
            <YAxis type="category" dataKey="name" width={120} fontSize={11} tick={makeAtRiskTick(teamChartData, atRiskUserIds, adjustedUserIds)} />
            <Tooltip content={<TeamDistTooltip />} />
            <Legend formatter={(v) => TYPE_LABELS[v] || v} wrapperStyle={{ fontSize: 11 }} />
            {teamChartData.map((row) => atRiskUserIds.has(row.user_id) ? (
              <ReferenceArea key={row.user_id} y1={row.name} y2={row.name} fill="#dc2626" fillOpacity={0.08} stroke="none" data-testid={`insights-atrisk-band-${row.user_id}`} />
            ) : null)}
            {Object.keys(TYPE_COLORS).map((t) => (
              <Bar key={t} dataKey={t} stackId="a" fill={TYPE_COLORS[t]} name={t}>
                <LabelList dataKey={t} content={makeSegmentLabel(t, teamChartData)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        {teamChartData.length === 0 && !loading && <div className="text-xs text-slate-400 italic py-4 text-center">No approved leaves in {year} yet.</div>}
      </div>

      {/* Individual Breakdown toggle + Monthly Distribution */}
      <div className="card-flat p-4" data-testid="chart-monthly-distribution">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5"><TrendingUp size={14} />Monthly distribution</div>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-56" data-testid="insights-individual-select"><SelectValue placeholder="Everyone (top 8)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone (top 8 by volume)</SelectItem>
              {peopleForSelect.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mb-3">
          {selectedUser === "all" ? "How many leave days the top people have taken, month by month." : "Selected person's leave days by category, month by month."}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          {selectedUser === "all" ? (
            <LineChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis allowDecimals={true} fontSize={11} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {topPeople.map((uid, i) => (
                <Line key={uid} type="monotone" dataKey={data?.user_names?.[uid]} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          ) : (
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis allowDecimals={true} fontSize={11} />
              <Tooltip />
              <Legend formatter={(v) => TYPE_LABELS[v] || v} wrapperStyle={{ fontSize: 11 }} />
              {Object.keys(TYPE_COLORS).map((t) => (
                <Bar key={t} dataKey={t} stackId="a" fill={TYPE_COLORS[t]} name={t} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
        {selectedUser !== "all" && monthlyChartData.length === 0 && (
          <div className="text-xs text-slate-400 italic py-4 text-center">No approved leaves for this person in {year}.</div>
        )}

        {selectedUser !== "all" && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="individual-leave-list">
            <div className="label-overline mb-2">{peopleForSelect.find((p) => p.user_id === selectedUser)?.name || "This person"}&rsquo;s leave days ({individualLeaves.length})</div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {individualLeaves.map((l) => (
                <div key={l.id} className="flex items-center justify-between text-xs py-1.5" data-testid={`individual-leave-${l.id}`}>
                  <span className="text-slate-700">{formatDate(l.start_date)}{l.start_date !== l.end_date ? ` → ${formatDate(l.end_date)}` : ""}</span>
                  <Tag tone="slate">{TYPE_LABELS[l.leave_type] || l.leave_type}</Tag>
                  <span className="text-slate-400 capitalize">{l.status}</span>
                </div>
              ))}
              {individualLeaves.length === 0 && <div className="text-xs text-slate-400 italic py-3">No leave records.</div>}
            </div>
          </div>
        )}
      </div>

      {/* Leave Balances (moved from the main tab) */}
      {balances && (
        <div className="card-flat overflow-hidden" data-testid="team-balances">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-1">
            <div className="label-overline">Leave balances · {balances.year}</div>
            <div className="flex items-center gap-3">
              <div className="text-[11px] text-slate-400 inline-flex items-center gap-1" data-testid="adjusted-legend">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> amber = manually adjusted
              </div>
              {isFounder && (
                <div className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Pencil size={10} /> click the pencil next to a value to adjust</div>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-5 py-2 label-overline">Member</th>
                  {Object.keys(TYPE_LABELS).map((t) => <th key={t} className="px-3 py-2 label-overline">{TYPE_LABELS[t]}</th>)}
                </tr>
              </thead>
              <tbody>
                {balances.users.map((u) => {
                  const flagged = atRiskUserIds.has(u.user_id);
                  return (
                    <tr key={u.user_id} className={`border-t border-slate-100 hover-row ${flagged ? "bg-red-50/70" : ""}`} data-testid={`team-balance-${u.user_id}`}>
                      <td className="px-5 py-2">
                        <div className={flagged ? "text-red-700 font-medium" : "text-slate-800"}>{u.name}</div>
                        <div className="text-xs text-slate-400">{u.business_unit || "—"}</div>
                      </td>
                      {Object.keys(TYPE_LABELS).map((t) => {
                        const b = u.balances.find((x) => x.leave_type === t);
                        if (!b) return <td key={t} className="px-3 py-2 text-slate-400">—</td>;
                        return (
                          <td key={t} className={`px-3 py-2 font-mono ${b.adjusted ? "bg-amber-50" : ""} ${leaveBarTone(b.remaining)}`}>
                            <span
                              className="inline-flex items-center gap-1.5"
                              title={b.adjusted ? "Includes a manual founder adjustment" : undefined}
                              data-testid={b.adjusted ? `balance-adjusted-${u.user_id}-${t}` : `balance-${u.user_id}-${t}`}
                            >
                              {b.taken}/{b.entitled}
                              {b.adjusted && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block shrink-0" />
                              )}
                              {isFounder && (
                                <button
                                  type="button"
                                  onClick={() => openAdjust(u, t, b)}
                                  className="text-slate-400 hover:text-orange-600 transition-colors"
                                  data-testid={`adjust-balance-btn-${u.user_id}-${t}`}
                                  title="Adjust balance (founder only)"
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* High-Risk Patterns */}
      <div className="card-flat p-4" data-testid="high-risk-panel">
        <div className="text-sm font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5 text-orange-700"><AlertTriangle size={14} />High-risk patterns</div>
        <div className="text-xs text-slate-500 mb-3">Rule-based: 3+ consecutive Mondays/Fridays off, or 3+ bridge-leave days taken this year (around your custom holiday calendar).</div>
        {(data?.high_risk || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No flagged patterns in {year}.</div>
        ) : (
          <div className="space-y-2">
            {data.high_risk.map((hr) => (
              <div key={hr.user_id} className="flex items-start gap-3 p-2.5 bg-orange-50/60 border border-orange-100 rounded-sm" data-testid={`high-risk-${hr.user_id}`}>
                <span className="text-sm font-medium text-slate-800 w-32 shrink-0">{hr.name}</span>
                <div className="flex flex-wrap gap-1.5">
                  {hr.patterns.map((p, i) => (
                    <Tag key={i} tone="orange">
                      {p.type === "frequent_bridge" ? `${p.count} bridge leaves` : `${p.count}× consecutive ${p.weekday}s`}
                    </Tag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Forecast */}
      <div className="card-flat p-4" data-testid="forecast-panel">
        <div className="text-sm font-semibold text-slate-800 mb-1">Likely leave dates · next 3 months</div>
        <div className="text-xs text-slate-500 mb-3">Projected from each flagged person&rsquo;s own weekday habit — a heuristic, not a guarantee.</div>
        {(data?.forecast || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">Nobody currently shows a strong enough pattern to forecast.</div>
        ) : (
          <div className="space-y-2">
            {data.forecast.map((f) => (
              <div key={f.user_id} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded-sm" data-testid={`forecast-${f.user_id}`}>
                <span className="font-medium text-slate-800">{f.name} <span className="text-slate-400 font-normal">· {f.based_on}</span></span>
                <span className="text-slate-500 font-mono">Next {f.weekday}: {f.likely_dates[0] ? formatDate(f.likely_dates[0]) : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Founder-only balance adjustment dialog */}
      <Dialog open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="adjust-balance-dialog">
          <DialogHeader>
            <DialogTitle>Adjust leave balance</DialogTitle>
          </DialogHeader>
          {adjustTarget && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">
                Founder-only adjustment — logged for audit. Adjusting the taken count corrects an error in recorded usage; adjusting the entitlement changes this person&rsquo;s annual cap only.
              </div>
              <div className="rounded-sm bg-slate-50 p-3 text-sm space-y-1" data-testid="adjust-balance-current-state">
                <div className="text-slate-800 font-medium">{adjustTarget.name} <span className="text-slate-400 font-normal">· year {year}</span></div>
                <div className="text-slate-500">{TYPE_LABELS[adjustTarget.leave_type]} · Taken: {adjustTarget.taken} / Entitled: {adjustTarget.entitled} / Remaining: <span className={leaveBarTone(adjustTarget.remaining)}>{adjustTarget.remaining}</span></div>
              </div>
              <div>
                <Label className="label-overline">Adjust Taken (days)</Label>
                <Input
                  type="number"
                  step="0.5"
                  placeholder="e.g. -1 to remove a wrongly logged day, -0.5 for half a day"
                  className="rounded-sm mt-1"
                  value={adjustTakenDelta}
                  onChange={(e) => setAdjustTakenDelta(e.target.value)}
                  data-testid="adjust-balance-taken-input"
                />
              </div>
              <div>
                <Label className="label-overline">Adjust Entitlement (days)</Label>
                <Input
                  type="number"
                  step="0.5"
                  placeholder="e.g. 2 to grant extra days"
                  className="rounded-sm mt-1"
                  value={adjustEntitlementDelta}
                  onChange={(e) => setAdjustEntitlementDelta(e.target.value)}
                  data-testid="adjust-balance-entitlement-input"
                />
              </div>
              <div className="rounded-sm border border-slate-200 p-3 text-sm space-y-1" data-testid="adjust-balance-preview">
                <div className="label-overline text-slate-400">New balance preview</div>
                <div className="text-slate-700">Taken: <span className="font-medium">{previewTaken}</span> / Entitled: <span className="font-medium">{previewEntitled}</span> / Remaining: <span className={`font-medium ${leaveBarTone(previewRemaining)}`}>{previewRemaining}</span></div>
              </div>
              <div>
                <Label className="label-overline">Reason</Label>
                <Textarea
                  rows={2}
                  className="rounded-sm mt-1"
                  placeholder="e.g. leave was logged in error / compensation for extra Saturday worked"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  data-testid="adjust-balance-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAdjustTarget(null)} data-testid="adjust-balance-cancel">Cancel</Button>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={submitAdjust} disabled={adjusting} data-testid="adjust-balance-submit">
              {adjusting ? "Saving…" : "Apply adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
